/**
 * Product Mapper
 *
 * Data transformation utilities for product entities following clean architecture principles.
 * Handles mapping between DTOs and domain models.
 *
 * @module productMapper
 */

import {ProductDTO} from '../models/product.dto';
import {Product} from '../models/product.model';
import {LoggerService} from '../../../Services/common/LoggerService';

export type {Product};

function ensureLeadingSlash (url: string | undefined | null): string {
  if (!url || typeof url !== 'string') {return '';}
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/')) {
    return url;
  }
  return `/${url}`;
}

function normalizeImages (dto: any): string[] {
  // Accept arrays of strings or objects with common url keys
  if (Array.isArray(dto?.images)) {
    return dto.images
      .map((img: any) => {
        if (typeof img === 'string' && img) {return ensureLeadingSlash(img);}
        if (img && typeof img === 'object') {
          const candidate = img.url || img.src || img.path || img.image_url || img.image;
          return candidate ? ensureLeadingSlash(candidate) : '';
        }
        return '';
      })
      .filter((url: string) => url && url.trim() !== '') as string[];
  }

  // Single image fields
  const single = dto?.image_url || dto?.image;
  if (typeof single === 'string' && single) {return [ensureLeadingSlash(single)];}

  // Media collections
  if (Array.isArray(dto?.media)) {
    return dto.media
      .map((m: any) => (typeof m === 'string' ? m : (m?.url || m?.src || m?.path)))
      .filter(Boolean)
      .map((s: string) => ensureLeadingSlash(s))
      .filter((url: string) => url && url.trim() !== '');
  }

  return [];
}

/**
 * Map ProductDTO to Product model
 * @param dto - Product DTO from API
 * @returns Product domain model
 */
export const mapProductDTOToModel = (dto: ProductDTO): Product => {
  LoggerService.debug('Mapping Product DTO to Model', dto, 'ProductMapper');

  return {
    id: dto.id || 0,
    name: dto.name || '',
    // Support both legacy string type and nested type object
    type: (typeof dto.type === 'string' && dto.type)
      ? dto.type
      : (dto.type && typeof dto.type === 'object' && 'name' in dto.type ? (dto.type as any).name : 'غير محدد'),
    barcode: dto.barcode || '',
    brandName: dto.brand?.name || '',
    serialCount: dto.serials?.length || 0,
    tags: dto.tags?.map(t => t.name) || [],
    price: typeof dto.price === 'string' ? Number(dto.price) : (dto.price || 0),
    images: normalizeImages(dto),
    hasSerial: dto.has_serial || false
  };
};

/**
 * Map Product model to ProductDTO
 * @param product - Product domain model
 * @returns Product DTO for API
 */
export const mapProductToDTO = (product: Product): ProductDTO => ({
  id: product.id,
  name: product.name,
  type: product.type,
  barcode: product.barcode,
  price: product.price,
  has_serial: product.hasSerial,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  brand: {id: 0, name: product.brandName},
  tags: product.tags.map((tag, index) => ({id: index, name: tag, slug: tag.toLowerCase()})),
  serials: [],
  images: product.images || []
});

/**
 * Map array of ProductDTOs to Product models
 * @param dtos - Array of Product DTOs
 * @returns Array of Product models
 */
export const mapProductsDTOToModel = (dtos: ProductDTO[]): Product[] =>
  dtos.map(mapProductDTOToModel);

/**
 * Map array of Product models to ProductDTOs
 * @param products - Array of Product models
 * @returns Array of Product DTOs
 */
export const mapProductsToDTO = (products: Product[]): ProductDTO[] =>
  products.map(mapProductToDTO);

// Export mapper object for convenience
export const productMapper = {
  mapProductDTOToModel,
  mapProductToDTO,
  mapProductsDTOToModel,
  mapProductsToDTO
};
